import React, { ChangeEvent, useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import "../admin-ui.css";

type QuestionBankMetadata = {
  type?: "test" | "task";
  specialty: string;
  directionId: number | string | null;
  directionName?: string;
  questionsCount: number;
  count?: number;
  tasksCount?: number;
  tasksWithoutImagesCount?: number | null;
  fileName: string;
  importedAt: string;
};

type BankType = "test" | "task";

const AdminQuestionsPage: React.FC = () => {
  const [bankType, setBankType] = useState<BankType>("test");
  const [specialty, setSpecialty] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [banks, setBanks] = useState<QuestionBankMetadata[]>([]);
  const [message, setMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const loadBanks = () => {
    fetch(`/api/question-banks?type=${bankType}`)
      .then((res) => res.json())
      .then((data: QuestionBankMetadata[]) => setBanks(data))
      .catch((err) => console.error("Ошибка загрузки списка баз вопросов:", err));
  };

  useEffect(() => {
    loadBanks();
  }, [bankType]);

  const getBankCountText = (bank: QuestionBankMetadata) => {
    const total = bank.count ?? bank.tasksCount ?? bank.questionsCount ?? 0;

    if (
      bankType === "task" &&
      typeof bank.tasksWithoutImagesCount === "number"
    ) {
      return `${bank.tasksWithoutImagesCount} без картинок / ${total} всего`;
    }

    return String(total);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(event.target.files?.[0] || null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage("Выберите JSON-файл");
      return;
    }

    const formData = new FormData();
    formData.append("specialty", specialty);
    formData.append("file", selectedFile);
    formData.append("type", bankType);

    setIsUploading(true);
    setMessage("");

    try {
      const response = await fetch(
        `/api/question-banks/import?type=${bankType}`,
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || "Ошибка загрузки базы");
      }

      setMessage(
        `База загружена: ${
          data.bank.count ?? data.bank.tasksCount ?? data.bank.questionsCount
        } ${bankType === "task" ? "задач" : "вопросов"}`
      );
      setSelectedFile(null);
      loadBanks();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Ошибка загрузки базы";
      setMessage(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteBank = async (bank: QuestionBankMetadata) => {
    const confirmed = window.confirm(
      `Удалить базу "${bank.specialty}" (${bank.fileName})?`
    );

    if (!confirmed) return;

    setMessage("");

    try {
      const response = await fetch(
        `/api/question-banks/${encodeURIComponent(bank.fileName)}?type=${bankType}`,
        { method: "DELETE" }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Не удалось удалить базу");
      }

      setMessage("База удалена");
      loadBanks();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Не удалось удалить базу");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-inner">
        <AdminNav section="questions" />

        <h2>Базы</h2>

        <div className="admin-nav">
          <button
            type="button"
            className={bankType === "test" ? "active" : ""}
            onClick={() => {
              setBankType("test");
              setMessage("");
            }}
          >
            Базы тестов
          </button>
          <button
            type="button"
            className={bankType === "task" ? "active" : ""}
            onClick={() => {
              setBankType("task");
              setMessage("");
            }}
          >
            Базы ситуационных задач
          </button>
        </div>

        <div
          className="admin-card admin-toolbar"
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            placeholder="Специальность"
            value={specialty}
            onChange={(event) => setSpecialty(event.target.value)}
            style={{ padding: "8px 12px", minWidth: "260px" }}
          />

          <label
            className={`admin-file-button ${
              isUploading ? "admin-file-button-disabled" : ""
            }`}
            style={{
              border: "1px solid #777",
              padding: "8px 12px",
              cursor: isUploading ? "default" : "pointer",
              backgroundColor: isUploading ? "#eee" : "#fff",
            }}
          >
            Выбрать JSON
            <input
              type="file"
              accept=".json,application/json"
              onChange={handleFileChange}
              disabled={isUploading}
              style={{ display: "none" }}
            />
          </label>

          <button
            className="admin-button admin-button-primary"
            onClick={handleUpload}
            disabled={isUploading}
          >
            {bankType === "task"
              ? "Загрузить базу задач"
              : "Загрузить базу вопросов"}
          </button>

          {selectedFile && (
            <span className="admin-message">{selectedFile.name}</span>
          )}
          {message && <span className="admin-message">{message}</span>}
        </div>

        <div className="admin-table-wrap">
          <table
            className="admin-table"
            border={1}
            cellPadding={8}
            style={{
              borderCollapse: "collapse",
              width: "100%",
              marginTop: "20px",
            }}
          >
            <thead>
              <tr>
                <th>Специальность</th>
                <th>Количество</th>
                <th>Файл</th>
                <th>Действия</th>
              </tr>
            </thead>

            <tbody>
              {banks.length === 0 ? (
                <tr>
                  <td colSpan={4} className="admin-table-empty">
                    {bankType === "task"
                      ? "Нет загруженных баз ситуационных задач"
                      : "Нет загруженных баз тестов"}
                  </td>
                </tr>
              ) : (
                banks.map((bank) => (
                  <tr key={bank.fileName}>
                    <td>{bank.specialty}</td>
                    <td>{getBankCountText(bank)}</td>
                    <td>{bank.fileName}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-button admin-button-danger"
                        onClick={() => handleDeleteBank(bank)}
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminQuestionsPage;
